// The native skin: the slot contract rendered the way chatgpt.com and
// claude.ai render — real markdown prose, reasoning collapsed by default, a
// tool call folded onto one row, attribution below the reply.
//
// This file is the whole of what makes these twelve components a *skin*. It
// adds no styling and no behaviour; it maps slot names to the elements that
// already exist, so that the composition can ask for "prose" without knowing
// that `std-turn-prose` is what answers.
//
// The `Implements` assertions below are the contract's teeth. Each one fails
// to compile if a component's props stop accepting what the contract promises
// for its slot — which is how a slot-props change lands as a build error here
// rather than as a blank row in a review card.

import { TurnSlots } from "../thread/slots";
import { TurnAsk } from "./ask";
import { TurnAttachment } from "./attachment";
import { TurnBar } from "./bar";
import { TurnKvGrid } from "./kv-grid";
import { TurnNote } from "./note";
import { TurnPlan } from "./plan";
import { TurnPrompt } from "./prompt";
import { TurnProse } from "./prose";
import { TurnResult } from "./result";
import { TurnTermBlock } from "./term-block";
import { TurnThought } from "./thought";
import { TurnTool } from "./tool";

// Compile-time only: nothing reads these, and removing one removes a guard.
type _Prompt = TurnSlots.Implements<TurnSlots.Props["prompt"], TurnPrompt.Props>;
type _Prose = TurnSlots.Implements<TurnSlots.Props["prose"], TurnProse.Props>;
type _Thought = TurnSlots.Implements<TurnSlots.Props["thought"], TurnThought.Props>;
type _Tool = TurnSlots.Implements<TurnSlots.Props["tool"], TurnTool.Props>;
type _Ask = TurnSlots.Implements<TurnSlots.Props["ask"], TurnAsk.Props>;
type _Plan = TurnSlots.Implements<TurnSlots.Props["plan"], TurnPlan.Props>;
type _Attachment = TurnSlots.Implements<TurnSlots.Props["attachment"], TurnAttachment.Props>;
type _Note = TurnSlots.Implements<TurnSlots.Props["note"], TurnNote.Props>;
type _Result = TurnSlots.Implements<TurnSlots.Props["result"], TurnResult.Props>;
type _Bar = TurnSlots.Implements<TurnSlots.Props["bar"], TurnBar.Props>;
type _TermBlock = TurnSlots.Implements<TurnSlots.Props["term-block"], TurnTermBlock.Props>;
type _KvGrid = TurnSlots.Implements<TurnSlots.Props["kv-grid"], TurnKvGrid.Props>;

/** The native skin. `make` is exhaustive over `TurnSlots.Slot`: the default
 * arm is unreachable by construction, and TypeScript proves it through the
 * `never` binding — a slot added to the contract lands here as a compile
 * error, which is the point. */
export const NATIVE_SKIN: TurnSlots.Skin = {
  name: "native",
  make<S extends TurnSlots.Slot>(slot: S, props: TurnSlots.Props[S]): HTMLElement {
    switch (slot) {
      case "prompt":
        return TurnPrompt.makeWith(props as TurnSlots.Props["prompt"]);
      case "prose":
        return TurnProse.makeWith(props as TurnSlots.Props["prose"]);
      case "thought":
        return TurnThought.makeWith(props as TurnSlots.Props["thought"]);
      case "tool":
        return TurnTool.makeWith(props as TurnSlots.Props["tool"]);
      case "ask":
        return TurnAsk.makeWith(props as TurnSlots.Props["ask"]);
      case "plan":
        return TurnPlan.makeWith(props as TurnSlots.Props["plan"]);
      case "attachment":
        return TurnAttachment.makeWith(props as TurnSlots.Props["attachment"]);
      case "note":
        return TurnNote.makeWith(props as TurnSlots.Props["note"]);
      case "result":
        return TurnResult.makeWith(props as TurnSlots.Props["result"]);
      case "bar":
        return TurnBar.makeWith(props as TurnSlots.Props["bar"]);
      case "term-block":
        return TurnTermBlock.makeWith(props as TurnSlots.Props["term-block"]);
      case "kv-grid":
        return TurnKvGrid.makeWith(props as TurnSlots.Props["kv-grid"]);
      default: {
        const unreachable: never = slot;
        throw new Error(`native skin: unknown slot ${String(unreachable)}`);
      }
    }
  },
};
